import React, { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Spinner } from 'react-bootstrap';
import styled from 'styled-components';

import { DynamicFormFieldModel, DynamicFormModel } from 'models';

import { Button, ComponentSizes } from 'core/ui';

import { useApiClient } from 'features/api';

interface FileTimelineProps {
  examId?: number | null;
  fileIds?: number[];
  initiallyVisible?: boolean;
}

const TimelineWrapper = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
  overflow-x: auto; // Allow horizontal scrolling for the table
  border-top: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3] ?? '#ddd'};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  table-layout: fixed;
  white-space: normal;

  th,
  td {
    border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3] ?? '#ddd'};
    padding: 0.5rem;
    text-align: left;
    vertical-align: top;
    max-width: 200px;
    word-wrap: break-word;
  }

  th {
    background-color: ${({ theme }) => theme.colors.primary[600] ?? '#007bff'};
    color: white;
    position: sticky;
    top: 0; // Make header sticky
    z-index: 1;
  }

  td:first-child,
  th:first-child {
    position: sticky;
    left: 0; // Make first column sticky
    background-color: ${({ theme }) => theme.colors.palette.grayscale[0]}; // Match row background
    z-index: 1;
  }
  th:first-child {
    z-index: 2; // Ensure first header cell is above other sticky elements
    background-color: ${({ theme }) => theme.colors.primary[600] ?? '#007bff'}; // Match header background
  }

  tbody tr:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
  }
`;

const FormHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.palette.grayscale[2]};
  padding: 0.5rem 1rem;
  font-weight: bold;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3] ?? '#ccc'};
  border-bottom: none; // Avoid double border when expanded
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[3]};
  }
`;

const LoadingErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px; // Ensure it takes some space
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

// Helper to format date consistently using dayjs
const formatDateForHeader = (dateString: string | Date | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = dayjs(dateString); // dayjs handles string or Date object
    // Add validity check using dayjs
    if (!date.isValid()) {
      return 'Invalid Date';
    }
    // Example format: 09/01/2023 07:19
    return date.format('MM/DD/YYYY HH:mm');
  } catch (e) {
    console.error('Error parsing or formatting date with dayjs:', dateString, e);
    return 'Invalid Date';
  }
};

export const FileTimeline: React.FC<FileTimelineProps> = ({ examId, fileIds, initiallyVisible = true }) => {
  const apiClient = useApiClient();
  const [timelineData, setTimelineData] = useState<DynamicFormModel[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // State to manage expanded forms - default to collapsed (false)
  const [expandedForms, setExpandedForms] = useState<Record<string, boolean>>({});
  const [isVisible, setIsVisible] = useState(initiallyVisible);

  useEffect(() => {
    const fetchTimelineData = async () => {
      let entityType: string | null = null;
      let ids: number[] | null = null;

      // Prioritize fileIds if provided and not empty
      if (fileIds && fileIds.length > 0) {
        entityType = 'File';
        ids = fileIds;
      }
      // Otherwise, use examId if provided
      else if (examId != null && examId > 0) {
        entityType = 'Exam';
        ids = [examId];
      }

      // If neither valid prop is provided, clear data and return
      if (!entityType || !ids) {
        console.log('Timeline Fetch: No valid IDs provided (examId, fileIds):', examId, fileIds);
        setTimelineData(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      console.log('Timeline Fetch: Fetching for', entityType, ids);
      setIsLoading(true);
      setError(null);
      try {
        // Fetch data based on the determined entityType and ids
        const response = await apiClient.dynamicFormsClient.GetFormSubmissions(entityType, ids);
        console.log('Timeline Fetch: API Response:', response);
        setTimelineData(response ?? null);
      } catch (err) {
        console.error(`Error fetching timeline data for ${entityType} IDs:`, ids, err);
        setError('Failed to load timeline data.');
        setTimelineData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimelineData();
  }, [examId, fileIds, apiClient]);

  const processedData = useMemo(() => {
    console.log('Timeline Data Input:', timelineData);

    if (!timelineData || !Array.isArray(timelineData) || timelineData.length === 0) {
      return { formGroups: {}, allHeaders: [], allFieldLabels: {} };
    }

    // 1. Group forms by name
    const formsByName: Record<string, DynamicFormModel[]> = {};
    timelineData.forEach((form) => {
      const formName = form.form?.name ?? 'Unnamed Form';
      if (!formsByName[formName]) {
        formsByName[formName] = [];
      }
      formsByName[formName].push(form);
    });
    console.log('Forms Grouped By Name:', formsByName);

    // 2. Collect all fields and group by date globally to get all unique dates (headers)
    const allFields = timelineData.flatMap((formModel) => formModel.fields || []);
    if (allFields.length === 0) {
      return { formGroups: {}, allHeaders: [], allFieldLabels: {} };
    }

    const groupedByDateGlobal: Record<string, DynamicFormFieldModel[]> = {};
    allFields.forEach((field) => {
      const dateKey = field.date ? formatDateForHeader(field.date) : 'No Date';
      if (!groupedByDateGlobal[dateKey]) {
        groupedByDateGlobal[dateKey] = [];
      }
      groupedByDateGlobal[dateKey].push(field);
    });

    const allHeaders = Object.keys(groupedByDateGlobal).sort((a, b) => {
      if (a === 'No Date' || a === 'Invalid Date') return 1;
      if (b === 'No Date' || b === 'Invalid Date') return -1;
      try {
        const dateA = dayjs(a, 'MM/DD/YYYY HH:mm'); // Parse using the format we created
        const dateB = dayjs(b, 'MM/DD/YYYY HH:mm');
        if (dateA.isValid() && dateB.isValid()) {
          return dateB.valueOf() - dateA.valueOf();
        }
        return b.localeCompare(a);
      } catch (e) {
        console.error('Error comparing date headers:', a, b, e);
        return b.localeCompare(a);
      }
    });
    console.log('All Headers (Sorted Dates):', allHeaders);

    // 3. Process each form group
    const formGroups: Record<
      string,
      {
        rows: Record<string, Record<string, string>>; // { [fieldName]: { [dateHeader]: value } }
        fieldLabels: Record<string, string>; // { [fieldName]: label }
        fieldOrder: string[]; // Maintain field order within the form
        activeHeaders: string[]; // Headers with actual data for THIS form
      }
    > = {};

    const allFieldLabels: Record<string, string> = {}; // Collect all unique field labels globally

    Object.keys(formsByName).forEach((formName) => {
      const formsInGroup = formsByName[formName];
      const fieldsInGroup = formsInGroup.flatMap((f) => f.fields || []);

      const uniqueFields: Record<string, string> = {};
      const fieldOrder: string[] = [];
      fieldsInGroup.forEach((field) => {
        if (!uniqueFields[field.name]) {
          uniqueFields[field.name] = field.label ?? field.name;
          allFieldLabels[field.name] = field.label ?? field.name; // Add to global list
          fieldOrder.push(field.name); // Preserve order of appearance within the form
        }
      });
      // Optionally sort fieldOrder alphabetically if needed: fieldOrder.sort();

      const rows: Record<string, Record<string, string>> = {};
      fieldOrder.forEach((fieldName) => {
        rows[fieldName] = {};
        allHeaders.forEach((headerDate) => {
          // Find the latest field value for this fieldName and headerDate within the group
          let latestValue: string | undefined;
          let latestDate: dayjs.Dayjs | null = null;

          fieldsInGroup.forEach((field) => {
            if (field.name === fieldName) {
              const fieldDateKey = field.date ? formatDateForHeader(field.date) : 'No Date';
              if (fieldDateKey === headerDate) {
                const currentFieldDate = field.date ? dayjs(field.date) : null;
                // Update if this field is later than the current latest found for this date header
                if (currentFieldDate?.isValid()) {
                  if (!latestDate || currentFieldDate.isAfter(latestDate)) {
                    latestDate = currentFieldDate;
                    latestValue = field.value;
                  }
                } else if (!latestDate && latestValue === undefined) {
                  // Handle 'No Date' or invalid dates - take the first one found
                  latestValue = field.value;
                }
              }
            }
          });
          rows[fieldName][headerDate] = latestValue ?? '-';
        });
      });

      // Determine active headers for THIS form group
      const activeHeaders = allHeaders.filter((headerDate) => {
        // Check if any field in this group has a value for this headerDate
        return fieldOrder.some((fieldName) => rows[fieldName]?.[headerDate] !== '-');
      });

      formGroups[formName] = {
        rows,
        fieldLabels: uniqueFields,
        fieldOrder,
        activeHeaders, // Store the active headers
      };
    });
    console.log('Processed Form Groups:', formGroups);

    const finalResult = { formGroups, allHeaders, allFieldLabels };
    console.log('Final Processed Data:', finalResult);

    return finalResult;
  }, [timelineData]);

  // Effect to reset expanded state when data changes
  useEffect(() => {
    setExpandedForms({}); // Collapse all when data reloads
  }, [timelineData]);

  const toggleFormExpansion = (formName: string) => {
    setExpandedForms((prev) => ({
      ...prev,
      [formName]: !prev[formName],
    }));
  };

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  if (isLoading) {
    return (
      <LoadingErrorContainer>
        <Spinner size="sm" /> Loading Timeline...
      </LoadingErrorContainer>
    );
  }

  if (error) {
    return <LoadingErrorContainer>{error}</LoadingErrorContainer>;
  }

  const { formGroups, allHeaders, allFieldLabels } = processedData as {
    formGroups: Record<
      string,
      {
        rows: Record<string, Record<string, string>>;
        fieldLabels: Record<string, string>;
        fieldOrder: string[];
        activeHeaders: string[]; // Add type here too
      }
    >;
    allHeaders: string[];
    allFieldLabels: Record<string, string>;
  };

  // Check if there is any data to display
  const hasData = Object.keys(formGroups).length > 0 && allHeaders.length > 0;
  const hasAnyForms = Object.keys(formGroups).length > 0;

  return (
    <TimelineWrapper>
      <TimelineHeader>
        <Button size={ComponentSizes.SMALL} onClick={toggleVisibility}>
          {isVisible ? 'Hide' : 'Show'} Timeline
        </Button>
      </TimelineHeader>

      {isVisible && (
        <>
          {/* Check if there are any forms to display */}
          {!hasAnyForms && !isLoading && !error && <LoadingErrorContainer>No timeline data available.</LoadingErrorContainer>}

          {hasAnyForms &&
            Object.keys(formGroups)
              .sort() // Sort form names alphabetically
              .map((formName) => {
                const group = formGroups[formName];
                const fieldNames = group.fieldOrder;
                const isExpanded = expandedForms[formName] ?? false; // Default to collapsed
                const activeHeadersForGroup = group.activeHeaders; // Use the calculated active headers

                // Check if the current form group has any fields AND any active data points
                if (fieldNames.length === 0 && activeHeadersForGroup.length === 0) {
                  // Render a simpler header if there are no fields and no data columns to show
                  return (
                    <FormHeader key={formName} onClick={() => toggleFormExpansion(formName)}>
                      <strong>{formName}</strong> (No fields or data points)
                      <span>{isExpanded ? '[-]' : '[+]'}</span>
                    </FormHeader>
                  );
                }

                return (
                  <div key={formName} style={{ marginBottom: '1rem' }}>
                    {/* Clickable Form Header */}
                    <FormHeader onClick={() => toggleFormExpansion(formName)}>
                      <strong>{formName}</strong>
                      <span>{isExpanded ? '[-]' : '[+]'}</span>
                    </FormHeader>

                    {/* Conditionally Rendered Table for the Form Group */}
                    {isExpanded && (
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Field</th>
                            {activeHeadersForGroup.map((header) => (
                              <th key={header}>{header}</th>
                            ))}
                            {activeHeadersForGroup.length === 0 && fieldNames.length > 0 && (
                              <th>No Data Points</th> // Show if fields exist but no active columns
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {fieldNames.map((fieldName) => (
                            <tr key={`${formName}-${fieldName}`}>
                              <td>{group.fieldLabels[fieldName] ?? fieldName}</td>
                              {activeHeadersForGroup.map((header) => (
                                <td key={`${formName}-${fieldName}-${header}`}>{group.rows[fieldName]?.[header] ?? '-'}</td>
                              ))}
                              {/* Render placeholder cell if no active headers */}
                              {activeHeadersForGroup.length === 0 && <td>-</td>}
                            </tr>
                          ))}
                          {/* Message if form has fields but no data points */}
                          {activeHeadersForGroup.length === 0 && fieldNames.length > 0 && (
                            <tr>
                              <td
                                colSpan={1} // Colspan is 1 because only 'Field' header exists
                                style={{ textAlign: 'center', fontStyle: 'italic' }}
                              >
                                No data points found for the fields in this form.
                              </td>
                            </tr>
                          )}
                          {/* Message if form has no fields but there are date headers */}
                          {fieldNames.length === 0 && activeHeadersForGroup.length > 0 && (
                            <tr>
                              <td
                                colSpan={activeHeadersForGroup.length + 1} // Colspan based on active headers
                                style={{ textAlign: 'center', fontStyle: 'italic' }}
                              >
                                This form had no fields submitted during these time points.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </StyledTable>
                    )}
                  </div>
                );
              })}

          {/* Keep original messages for overall loading/error states if needed outside the loop */}
          {/* Example: Error message if the initial fetch failed */}
          {error && !hasAnyForms && <LoadingErrorContainer>{error}</LoadingErrorContainer>}
        </>
      )}
    </TimelineWrapper>
  );
};
